import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { errorHandlerMiddleware, loadingStateMiddleware } from './middleware/errorHandler'

// API slices
import { authApi } from './api/authApi'
import { cigarApi } from './api/cigarApi'
import { humidorApi } from './api/humidorApi'
import { sessionApi } from './api/sessionApi'
import { userApi } from './api/userApi'
import { brandApi } from './api/brandApi'
import { submissionApi } from './api/submissionApi'
import { vitolaApi } from './api/vitolaApi'
import { adminApi } from './api/adminApi'
import { databaseApi } from './api/databaseApi'
import { cigarDetailApi } from './api/cigarDetailApi'
import { analyticsApi } from './api/analyticsApi'
import { wishlistApi } from './api/wishlistApi'
import { moderationApi, adminModerationApi } from '../features/moderation/api/moderationApi'
import { notificationApi } from './api/notificationApi'
import { feedbackApi } from './api/feedbackApi'
import { historyApi } from './api/historyApi'
import { ratingApi } from './api/ratingApi'
import { boxApi } from './api/boxApi'
import { importApi } from './api/importApi'
import { helpApi } from './api/helpApi'
import { subscriptionApi } from './api/subscriptionApi'
import { siteApi } from './api/siteApi'

// Feature slices
import authSlice from './slices/authSlice'
import uiSlice from './slices/uiSlice'
import collectionSlice from './slices/collectionSlice'
import offlineReducer from './slices/offlineSlice'

export const store = configureStore({
  reducer: {
    // Feature slices
    auth: authSlice,
    ui: uiSlice,
    collection: collectionSlice,

    offline: offlineReducer,

    // API slices
    [authApi.reducerPath]: authApi.reducer,
    [cigarApi.reducerPath]: cigarApi.reducer,
    [humidorApi.reducerPath]: humidorApi.reducer,
    [sessionApi.reducerPath]: sessionApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [brandApi.reducerPath]: brandApi.reducer,
    [submissionApi.reducerPath]: submissionApi.reducer,
    [vitolaApi.reducerPath]: vitolaApi.reducer,
    [adminApi.reducerPath]: adminApi.reducer,
    [databaseApi.reducerPath]: databaseApi.reducer,
    [cigarDetailApi.reducerPath]: cigarDetailApi.reducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [wishlistApi.reducerPath]: wishlistApi.reducer,
    [moderationApi.reducerPath]: moderationApi.reducer,
    [adminModerationApi.reducerPath]: adminModerationApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [feedbackApi.reducerPath]: feedbackApi.reducer,
    [historyApi.reducerPath]: historyApi.reducer,
    [ratingApi.reducerPath]: ratingApi.reducer,
    [boxApi.reducerPath]: boxApi.reducer,
    [importApi.reducerPath]: importApi.reducer,
    [helpApi.reducerPath]: helpApi.reducer,
    [subscriptionApi.reducerPath]: subscriptionApi.reducer,
    [siteApi.reducerPath]: siteApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    })
      .concat(errorHandlerMiddleware)
      .concat(loadingStateMiddleware)
      .concat(authApi.middleware)
      .concat(cigarApi.middleware)
      .concat(humidorApi.middleware)
      .concat(sessionApi.middleware)
      .concat(userApi.middleware)
      .concat(brandApi.middleware)
      .concat(submissionApi.middleware)
      .concat(vitolaApi.middleware)
      .concat(adminApi.middleware)
      .concat(databaseApi.middleware)
      .concat(cigarDetailApi.middleware)
      .concat(analyticsApi.middleware)
      .concat(wishlistApi.middleware)
      .concat(moderationApi.middleware)
      .concat(adminModerationApi.middleware)
      .concat(notificationApi.middleware)
      .concat(feedbackApi.middleware)
      .concat(historyApi.middleware)
      .concat(ratingApi.middleware)
      .concat(boxApi.middleware)
      .concat(importApi.middleware)
      .concat(helpApi.middleware)
      .concat(subscriptionApi.middleware)
      .concat(siteApi.middleware),
  devTools: process.env.NODE_ENV !== 'production',
})

// Setup listeners for RTK Query
setupListeners(store.dispatch)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export default store
